import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { customersAPI } from '../services/api';
import { Edit2, Trash2, User } from 'lucide-react';
import { createNavigationHelpers } from '../utils/navigationHelpers';
import { usePermissions } from '../hooks/usePermissions';
import ConfirmationModal from '../components/ConfirmationModal';

const Customers = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { goToEdit } = createNavigationHelpers('customers', navigate);
  const { customers: customerPerms } = usePermissions();

  // Fetch customers
  const { data: customersData, isLoading, error } = useQuery(
    ['customers', currentPage, pageSize],
    () => customersAPI.getAll({ page: currentPage, limit: pageSize }),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onSuccess: (data) => {
        console.log('Customers query success:', data);
      },
      onError: (error) => {
        console.error('Customers fetch error:', error);
        toast.error('Failed to load customers');
      }
    }
  );

  // Delete customer mutation
  const deleteMutation = useMutation(
    (uuid) => customersAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customers']);
        toast.success('Customer deleted successfully!');
        setDeleteModalOpen(false);
        setCustomerToDelete(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'An error occurred');
      },
    }
  );

  const handleEdit = (customer) => {
    goToEdit(customer.uuid);
  };

  const handleDelete = (customer) => {
    setCustomerToDelete(customer);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (customerToDelete) {
      deleteMutation.mutate(customerToDelete.uuid);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  // Debug logging
  console.log('=== CUSTOMERS DEBUG ===');
  console.log('Raw customersData:', customersData);
  console.log('customersData?.data:', customersData?.data);
  console.log('customersData?.data?.data:', customersData?.data?.data);
  
  // Extract customers from the correct path in the API response
  let customers = [];
  let pagination = { page: 1, limit: 10, total: 0, pages: 1 };
  
  // First check if we have the nested structure (data.data.data)
  if (Array.isArray(customersData?.data?.data)) {
    customers = customersData.data.data;
    pagination = customersData.data.pagination || pagination;
    console.log('Using nested data structure for customers');
  } 
  // Then check if we have a simpler structure (data.data)
  else if (Array.isArray(customersData?.data)) {
    customers = customersData.data;
    pagination = customersData.data.pagination || pagination;
    console.log('Using simple data structure for customers');
  }
  
  console.log('Extracted customers:', customers.length);
  console.log('Pagination info:', pagination);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load customers</div>
          <div className="text-gray-500 dark:text-gray-400 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Customers</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage customer information and relationships
            </p>
          </div>
        </div>
      </div>

      {/* Customers Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {customers.map((customer, index) => (
          <div
            key={customer.uuid}
            className="card-modern group hover-lift animate-fade-in-up overflow-hidden"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex justify-between items-start mb-4 min-h-[3rem]">
              <div className="flex items-center space-x-3 min-w-0 flex-1 pr-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-glow group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                  {customer.name?.charAt(0)?.toUpperCase() || 'C'}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{customer.name}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate">{customer.email}</p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <span
                  className={`badge-responsive ${
                    customer.is_active
                      ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                      : 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400'
                  }`}
                >
                  {customer.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            
            <div className="space-y-2 mb-3 sm:mb-4">
              {customer.phone && (
                <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm">
                  <span className="font-medium">Phone:</span> {customer.phone}
                </p>
              )}
              {customer.address && (
                <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm line-clamp-2">
                  <span className="font-medium">Address:</span> {customer.address}
                </p>
              )}
            </div>
            
            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">
              <span>Joined: {new Date(customer.created_at).toLocaleDateString()}</span>
            </div>
            
            <div className="flex gap-2 sm:gap-3">
              {/* Edit Button - Only show if user can update customers */}
              {customerPerms.canUpdate && (
                <button
                  onClick={() => handleEdit(customer)}
                  className="flex-1 sm:flex-none text-indigo-600 hover:text-indigo-800 text-xs sm:text-sm font-medium flex items-center justify-center gap-1 py-1"
                >
                  <Edit2 className="h-3 w-3" />
                  Edit
                </button>
              )}
              
              {/* Delete Button - Only show if user can delete customers */}
              {customerPerms.canDelete && (
                <button
                  onClick={() => handleDelete(customer)}
                  className="flex-1 sm:flex-none text-red-600 hover:text-red-800 text-xs sm:text-sm font-medium flex items-center justify-center gap-1 py-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {customers.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No customers yet</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Get started by adding your first customer</p>
        </div>
      )}

      {/* Pagination Controls */}
      {customers.length > 0 && pagination.pages > 1 && (
        <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing <span className="font-semibold">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-semibold">{Math.min(currentPage * pageSize, pagination.total)}</span> of <span className="font-semibold">{pagination.total}</span> customers
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Previous
            </button>
            
            {/* Page numbers */}
            <div className="flex gap-1">
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    currentPage === page
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
              disabled={currentPage === pagination.pages}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCustomerToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete the customer "${customerToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Customers; 